/**
 * LessonLink - 授業スケジュールモジュール
 */
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import { showMessage, clearMessage, formatLessonDate, formatLessonDateShort, isInstructor, formatCurrency } from './utils';
import { showContactForLesson } from './contact';
import { getCurrentUserRole, getCurrentUserEmail, loadStudentSettings, getStudentDuration } from './settings';

type LessonClient = ReturnType<typeof generateClient<Schema>>;
let client: LessonClient;

function getClient() {
  if (!client) {
    client = generateClient<Schema>();
  }
  return client;
}

const PAST_PAGE_SIZE = 10;
let pastOffset = 0;
let allPastLessons: Schema['Lesson']['type'][] = [];
// email → displayName のマッピング（講師用の生徒名表示）
let studentNameMap: Map<string, string> = new Map();

/**
 * スケジュール機能を初期化
 */
export function initSchedule() {
  const bookingBtn = document.getElementById('booking-btn') as HTMLButtonElement;
  const bookingForm = document.getElementById('booking-form') as HTMLFormElement;
  const bookingCancel = document.getElementById('booking-cancel') as HTMLButtonElement;
  const bookingSection = document.getElementById('booking-section') as HTMLElement;
  const loadMoreBtn = document.getElementById('load-more-btn') as HTMLButtonElement;

  const yearSelect = document.getElementById('lesson-year') as HTMLSelectElement;
  const monthSelect = document.getElementById('lesson-month') as HTMLSelectElement;
  const daySelect = document.getElementById('lesson-day') as HTMLSelectElement;
  const hourSelect = document.getElementById('lesson-hour') as HTMLSelectElement;
  const minuteSelect = document.getElementById('lesson-minute') as HTMLSelectElement;
  const studentSelect = document.getElementById('lesson-student') as HTMLSelectElement;
  const studentSelectGroup = document.getElementById('student-select-group') as HTMLElement;

  // 年・月・時の選択肢を生成
  function populateSelects() {
    const now = new Date();
    const thisYear = now.getFullYear();
    const nextYear = thisYear + 1;

    // 年（今年・来年）
    yearSelect.innerHTML = '';
    [thisYear, nextYear].forEach((y) => {
      const opt = document.createElement('option');
      opt.value = String(y);
      opt.textContent = String(y);
      yearSelect.appendChild(opt);
    });

    // 月（1〜12）
    monthSelect.innerHTML = '';
    for (let m = 1; m <= 12; m++) {
      const opt = document.createElement('option');
      opt.value = String(m).padStart(2, '0');
      opt.textContent = String(m);
      monthSelect.appendChild(opt);
    }

    // 時（8〜21）
    hourSelect.innerHTML = '';
    for (let h = 8; h <= 21; h++) {
      const opt = document.createElement('option');
      opt.value = String(h).padStart(2, '0');
      opt.textContent = String(h);
      hourSelect.appendChild(opt);
    }

    // 初期値: 明日・現在時刻に近い時
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    yearSelect.value = String(tomorrow.getFullYear());
    monthSelect.value = String(tomorrow.getMonth() + 1).padStart(2, '0');
    updateDayOptions();
    daySelect.value = String(tomorrow.getDate()).padStart(2, '0');

    const currentHour = Math.max(8, Math.min(21, now.getHours()));
    hourSelect.value = String(currentHour).padStart(2, '0');
    minuteSelect.value = '00';
  }

  // 日の選択肢を年・月に応じて更新
  function updateDayOptions() {
    const year = parseInt(yearSelect.value);
    const month = parseInt(monthSelect.value);
    const daysInMonth = new Date(year, month, 0).getDate();
    const currentDay = daySelect.value;

    daySelect.innerHTML = '';
    for (let d = 1; d <= daysInMonth; d++) {
      const opt = document.createElement('option');
      opt.value = String(d).padStart(2, '0');
      opt.textContent = String(d);
      daySelect.appendChild(opt);
    }

    // 以前選択していた日を維持（範囲内なら）
    if (parseInt(currentDay) <= daysInMonth) {
      daySelect.value = currentDay;
    }
  }

  yearSelect.addEventListener('change', updateDayOptions);
  monthSelect.addEventListener('change', updateDayOptions);

  // 予約フォーム表示/非表示
  bookingBtn.addEventListener('click', async () => {
    populateSelects();
    await populateStudentSelect();
    bookingSection.classList.remove('hidden');
    bookingBtn.classList.add('hidden');
    const heading = bookingSection.querySelector('h2');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus();
    }
  });

  // 生徒セレクトを生成（講師のみ表示）
  async function populateStudentSelect() {
    const role = getCurrentUserRole();
    if (!isInstructor(role)) {
      studentSelectGroup.classList.add('hidden');
      return;
    }

    try {
      const { data: users } = await getClient().models.User.list();
      const students = users?.filter((u) => u.role === 'student') || [];

      if (students.length === 0) {
        studentSelectGroup.classList.remove('hidden');
        studentSelect.innerHTML = '<option value="">生徒がいません（先にユーザー管理から招待してください）</option>';
        return;
      }

      studentSelectGroup.classList.remove('hidden');
      studentSelect.innerHTML = students
        .map((s) => `<option value="${s.email}">${s.displayName}</option>`)
        .join('');
    } catch (error) {
      console.error('Load students error:', error);
    }
  }

  bookingCancel.addEventListener('click', () => {
    bookingSection.classList.add('hidden');
    bookingBtn.classList.remove('hidden');
    bookingBtn.focus();
    bookingForm.reset();
  });

  // 予約送信
  bookingForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    clearMessage();

    const year = parseInt(yearSelect.value);
    const month = parseInt(monthSelect.value);
    const day = parseInt(daySelect.value);
    const hour = parseInt(hourSelect.value);
    const minute = parseInt(minuteSelect.value);

    const selectedDate = new Date(year, month - 1, day, hour, minute);

    // 過去日時チェック
    if (selectedDate <= new Date()) {
      showMessage('過去の日時は予約できません');
      return;
    }

    const isoDate = selectedDate.toISOString();

    try {
      // 重複チェック
      const { data: existing } = await getClient().models.Lesson.list({
        filter: { date: { eq: isoDate } },
      });

      if (existing && existing.length > 0) {
        showMessage('同じ日時に予約があります');
        return;
      }

      // studentOwner を決定
      const role = getCurrentUserRole();
      let studentOwner: string | undefined;
      if (isInstructor(role)) {
        studentOwner = studentSelect.value || undefined;
        if (!studentOwner) {
          showMessage('生徒を選択してください');
          return;
        }
      } else {
        studentOwner = getCurrentUserEmail() || undefined;
      }

      // 作成
      await getClient().models.Lesson.create({
        date: isoDate,
        status: 'scheduled',
        studentOwner,
      });

      showMessage('授業を予約しました', 'success');
      bookingForm.reset();
      bookingSection.classList.add('hidden');
      bookingBtn.classList.remove('hidden');

      // メール通知（失敗しても予約は成功扱い）
      try {
        const sName = studentNameMap.get(studentOwner!) || studentOwner!;
        const sDuration = getStudentDuration(studentOwner!);
        await getClient().mutations.sendNotification({
          type: 'booked',
          date: isoDate,
          studentEmail: studentOwner!,
          studentName: sName,
          meetUrl: null,
          durationMinutes: sDuration,
        });
      } catch (notifyError) {
        console.error('Notification error:', notifyError);
      }

      await loadLessons();
    } catch (error) {
      console.error('Booking error:', error);
      showMessage('予約に失敗しました');
    }
  });

  // もっと見る
  loadMoreBtn.addEventListener('click', () => {
    pastOffset += PAST_PAGE_SIZE;
    renderPastLessons();
    // 新しく表示された最初の項目にフォーカス
    const pastList = document.getElementById('past-lessons') as HTMLElement;
    const items = pastList.querySelectorAll('li');
    if (items.length > pastOffset - PAST_PAGE_SIZE) {
      const newFirst = items[pastOffset - PAST_PAGE_SIZE];
      if (newFirst) {
        (newFirst as HTMLElement).setAttribute('tabindex', '-1');
        (newFirst as HTMLElement).focus();
      }
    }
  });

  // 授業一覧削除のイベント委譲
  const futureList = document.getElementById('future-lessons') as HTMLElement;
  futureList.addEventListener('click', async (e) => {
    const contactBtn = (e.target as HTMLElement).closest('[data-contact-id]') as HTMLElement;
    if (contactBtn) {
      const lessonId = contactBtn.dataset.contactId!;
      await showContactForLesson(lessonId);
      return;
    }

    const btn = (e.target as HTMLElement).closest('[data-delete-id]') as HTMLElement;
    if (!btn) return;

    const id = btn.dataset.deleteId!;
    const dateStr = btn.dataset.deleteDate!;
    const studentEmail = btn.dataset.deleteStudent!;
    const label = formatLessonDateShort(dateStr);

    if (!confirm(`${label} の授業を削除しますか？`)) return;

    try {
      await getClient().models.Lesson.delete({ id });
      showMessage('授業を削除しました', 'success');

      // メール通知（失敗しても削除は成功扱い）
      try {
        const sName = studentNameMap.get(studentEmail) || studentEmail;
        const sDuration = getStudentDuration(studentEmail);
        await getClient().mutations.sendNotification({
          type: 'cancelled',
          date: dateStr,
          studentEmail,
          studentName: sName,
          durationMinutes: sDuration,
        });
      } catch (notifyError) {
        console.error('Notification error:', notifyError);
      }

      await loadLessons();
    } catch (error) {
      console.error('Delete error:', error);
      showMessage('削除に失敗しました');
    }
  });

  // 過去の授業のイベント委譲（連絡帳 + 講師のみ削除）
  const pastList = document.getElementById('past-lessons') as HTMLElement;
  pastList.addEventListener('click', async (e) => {
    const contactBtn = (e.target as HTMLElement).closest('[data-contact-id]') as HTMLElement;
    if (contactBtn) {
      const lessonId = contactBtn.dataset.contactId!;
      await showContactForLesson(lessonId);
      return;
    }

    const deleteBtn = (e.target as HTMLElement).closest('[data-past-delete-id]') as HTMLElement;
    if (!deleteBtn) return;

    const id = deleteBtn.dataset.pastDeleteId!;
    const dateStr = deleteBtn.dataset.pastDeleteDate!;
    const studentEmail = deleteBtn.dataset.pastDeleteStudent!;
    const label = formatLessonDateShort(dateStr);

    if (!confirm(`${label} の授業をキャンセル扱いにしますか？\n請求から除外されます。`)) return;

    try {
      await getClient().models.Lesson.delete({ id });
      showMessage('授業を削除しました（キャンセル扱い）', 'success');

      // メール通知（失敗しても削除は成功扱い）
      try {
        const sName = studentNameMap.get(studentEmail) || studentEmail;
        const sDuration = getStudentDuration(studentEmail);
        await getClient().mutations.sendNotification({
          type: 'cancelled',
          date: dateStr,
          studentEmail,
          studentName: sName,
          durationMinutes: sDuration,
        });
      } catch (notifyError) {
        console.error('Notification error:', notifyError);
      }

      await loadLessons();
    } catch (error) {
      console.error('Delete past lesson error:', error);
      showMessage('削除に失敗しました');
    }
  });
}

/**
 * 授業一覧を読み込んで表示
 */
export async function loadLessons() {
  try {
    const { data: lessons } = await getClient().models.Lesson.list();
    if (!lessons) return;

    const role = getCurrentUserRole();
    const myEmail = getCurrentUserEmail();

    // 生徒設定を読み込み（授業時間・単価）
    const studentSettings = await loadStudentSettings();

    // 講師の場合、生徒名マッピングを構築
    if (isInstructor(role)) {
      const { data: users } = await getClient().models.User.list();
      studentNameMap.clear();
      users?.forEach((u) => {
        if (u.email && u.displayName) {
          studentNameMap.set(u.email, u.displayName);
        }
      });
    }

    // 生徒の場合、自分の授業のみフィルター
    const filtered = isInstructor(role)
      ? lessons
      : lessons.filter((l) => l.studentOwner === myEmail);

    const now = new Date();

    // 過去の授業ステータスを自動更新
    for (const lesson of filtered) {
      if (lesson.date && new Date(lesson.date) < now && lesson.status === 'scheduled') {
        await getClient().models.Lesson.update({
          id: lesson.id,
          status: 'completed',
        });
        (lesson as any).status = 'completed';
      }
    }

    // 今後と過去に分割
    const futureLessons = filtered
      .filter((l) => l.date && new Date(l.date) >= now)
      .sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime());

    allPastLessons = filtered
      .filter((l) => l.date && new Date(l.date) < now)
      .sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime());

    pastOffset = 0;

    renderFutureLessons(futureLessons);
    renderPastLessons();

    // 請求情報を表示
    renderBilling(allPastLessons, myEmail, role, studentSettings);
  } catch (error) {
    console.error('Load lessons error:', error);
    showMessage('授業一覧の読み込みに失敗しました');
  }
}

function renderFutureLessons(lessons: Schema['Lesson']['type'][]) {
  const container = document.getElementById('future-lessons') as HTMLElement;
  const emptyMsg = document.getElementById('future-empty') as HTMLElement;

  if (lessons.length === 0) {
    container.innerHTML = '';
    emptyMsg.classList.remove('hidden');
    return;
  }

  emptyMsg.classList.add('hidden');
  const role = getCurrentUserRole();
  container.innerHTML = lessons
    .map((l) => {
      const duration = getStudentDuration(l.studentOwner || '');
      const dateLabel = formatLessonDate(l.date!, duration);
      const studentName = isInstructor(role) && l.studentOwner
        ? ` — ${studentNameMap.get(l.studentOwner) || l.studentOwner}`
        : '';
      return `<li class="flex items-center justify-between py-2 border-b border-gray-200">
        <span>${dateLabel}${studentName}</span>
        <div class="flex gap-2">
          <button type="button"
                  data-contact-id="${l.id}"
                  class="text-blue-600 hover:text-blue-800 text-sm px-2 py-1 rounded border border-blue-300 hover:bg-blue-50">
            連絡帳
          </button>
          <button type="button"
                  data-delete-id="${l.id}"
                  data-delete-date="${l.date}"
                  data-delete-student="${l.studentOwner || ''}"
                  class="text-red-600 hover:text-red-800 text-sm px-2 py-1 rounded border border-red-300 hover:bg-red-50">
            削除
          </button>
        </div>
      </li>`;
    })
    .join('');
}

function renderPastLessons() {
  const container = document.getElementById('past-lessons') as HTMLElement;
  const emptyMsg = document.getElementById('past-empty') as HTMLElement;
  const loadMoreBtn = document.getElementById('load-more-btn') as HTMLButtonElement;

  if (allPastLessons.length === 0) {
    container.innerHTML = '';
    emptyMsg.classList.remove('hidden');
    loadMoreBtn.classList.add('hidden');
    return;
  }

  emptyMsg.classList.add('hidden');

  const visible = allPastLessons.slice(0, pastOffset + PAST_PAGE_SIZE);
  const role = getCurrentUserRole();
  const isInstr = isInstructor(role);

  container.innerHTML = visible
    .map((l) => {
      const duration = getStudentDuration(l.studentOwner || '');
      const dateLabel = formatLessonDate(l.date!, duration);
      const studentName = isInstr && l.studentOwner
        ? ` — ${studentNameMap.get(l.studentOwner) || l.studentOwner}`
        : '';
      const deleteBtn = isInstr
        ? `<button type="button"
                  data-past-delete-id="${l.id}"
                  data-past-delete-date="${l.date}"
                  data-past-delete-student="${l.studentOwner || ''}"
                  class="text-red-600 hover:text-red-800 text-sm px-2 py-1 rounded border border-red-300 hover:bg-red-50">
            削除
          </button>`
        : '';
      return `<li class="flex items-center justify-between py-2 border-b border-gray-200">
        <span>${dateLabel}${studentName} ✓完了</span>
        <div class="flex gap-2">
          <button type="button"
                  data-contact-id="${l.id}"
                  class="text-blue-600 hover:text-blue-800 text-sm px-2 py-1 rounded border border-blue-300 hover:bg-blue-50">
            連絡帳
          </button>
          ${deleteBtn}
        </div>
      </li>`;
    })
    .join('');

  if (allPastLessons.length > visible.length) {
    loadMoreBtn.classList.remove('hidden');
  } else {
    loadMoreBtn.classList.add('hidden');
  }
}

/**
 * 請求情報を表示
 */
function renderBilling(
  pastLessons: Schema['Lesson']['type'][],
  myEmail: string | null,
  role: string | null,
  studentSettings: Map<string, { duration: number; price: number }>,
) {
  const billingSection = document.getElementById('billing-section') as HTMLElement;
  if (!billingSection) return;

  billingSection.innerHTML = '';
  const now = new Date();

  for (let offset = 0; offset < 3; offset++) {
    const targetDate = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const monthLabel = `${year}年${month + 1}月`;
    const monthStart = new Date(year, month, 1);
    const monthEnd = offset === 0 ? now : new Date(year, month + 1, 1);

    const details = document.createElement('details');
    details.className = 'border border-gray-200 rounded';

    const summary = document.createElement('summary');
    summary.className = 'p-3 bg-gray-50 cursor-pointer font-medium';

    const content = document.createElement('div');
    content.className = 'p-3';

    if (isInstructor(role)) {
      let totalAll = 0;
      const lines: string[] = [];
      const studentEmails = new Set(pastLessons.map((l) => l.studentOwner).filter(Boolean) as string[]);
      studentEmails.forEach((email) => {
        const studentLessons = pastLessons.filter((l) => l.studentOwner === email);
        const thisMonthLessons = studentLessons.filter((l) => {
          const d = new Date(l.date!);
          return d >= monthStart && d < monthEnd;
        });
        const count = thisMonthLessons.length;
        const settings = studentSettings.get(email);
        const price = settings?.price ?? 1200;
        const subtotal = count * price;
        totalAll += subtotal;
        const name = studentNameMap.get(email) || email;
        lines.push(`${name}: ${count}回 × ${formatCurrency(price)}円 = ${formatCurrency(subtotal)}円`);
      });

      summary.textContent = `${monthLabel}`;
      lines.forEach((line) => {
        const p = document.createElement('p');
        p.className = 'mb-1';
        p.textContent = line;
        content.appendChild(p);
      });
      const totalP = document.createElement('p');
      totalP.className = 'font-bold';
      totalP.textContent = `合計: ${formatCurrency(totalAll)}円`;
      content.appendChild(totalP);
    } else {
      const settings = myEmail ? studentSettings.get(myEmail) : undefined;
      const price = settings?.price ?? 1200;
      const myLessons = pastLessons.filter((l) => {
        const d = new Date(l.date!);
        return d >= monthStart && d < monthEnd;
      });
      const count = myLessons.length;
      const total = count * price;

      summary.textContent = `${monthLabel}`;
      const countP = document.createElement('p');
      countP.className = 'mb-1';
      countP.textContent = `${count}回`;
      content.appendChild(countP);
      const priceP = document.createElement('p');
      priceP.className = 'mb-1';
      priceP.textContent = `× ${formatCurrency(price)}円`;
      content.appendChild(priceP);
      const totalP = document.createElement('p');
      totalP.className = 'font-bold';
      totalP.textContent = `= ${formatCurrency(total)}円`;
      content.appendChild(totalP);
    }

    details.appendChild(summary);
    details.appendChild(content);
    billingSection.appendChild(details);
  }
}
